import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, Save, QrCode, Trash2 } from 'lucide-react';

const DEFAULT_SETTINGS = {
  logo_url: '',
  logo_size_percent: 20,
  store_name: '',
  store_subtitle: '',
  phone_display: '',
  whatsapp_number: '',
  whatsapp_message: '',
  telegram_default_url: '',
  instagram_url: '',
  wifi_ssid: '',
  wifi_password: '',
  wifi_encryption: 'WPA',
  accent_color: '#D4AF37',
  text_color: '#FFFFFF',
  qr_size_percent: 100,
  qr_label_size: 100,
  qr_label_position: 'below',
  back_qr_size_percent: 100,
  back_qr_label_size: 100,
  back_qr_label_position: 'below',
  brand_title_size: 100,
  brand_title_offset: 0,
  phone_size_percent: 100,
  whatsapp_active: true,
  telegram_active: true,
  instagram_active: true,
  wifi_active: true,
  main_channel_active: false,
  website_url: '',
  website_active: false,
  whatsapp_label: 'WhatsApp',
  telegram_label: 'Telegram',
  instagram_label: 'Instagram',
  wifi_label: 'WiFi',
  main_channel_label: 'Ana Kanal',
  website_label: 'Website',
  brand_website_active: false,
  brand_website_pattern: '',
  brand_website_label: 'Website',
  brand_telegram_source: 'brand_link',
  brand_telegram_custom_url: '',
  use_dynamic_qr: false
};

export default function QRPosterSettings({ selectedTeam }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [backgrounds, setBackgrounds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [newBgName, setNewBgName] = useState('');
  const [newBgUrl, setNewBgUrl] = useState('');

  useEffect(() => {
    if (selectedTeam) {
      loadSettings();
      loadBackgrounds();
    }
  }, [selectedTeam]);

  const loadSettings = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/qr-poster/settings`);
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch (err) {
      toast.error('Ayarlar yüklenemedi');
    }
  };

  const loadBackgrounds = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/qr-poster/backgrounds`);
      setBackgrounds(res.data);
    } catch (err) {
      toast.error('Arkaplanlar yüklenemedi');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/teams/${selectedTeam}/qr-poster/settings`, settings);
      toast.success('Ayarlar kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axiosInstance.post('/upload-media', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.url;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadImage(file);
      setSettings({ ...settings, logo_url: url });
      toast.success('Logo yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBgFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    try {
      const url = await uploadImage(file);
      setNewBgUrl(url);
      if (!newBgName) setNewBgName(file.name.replace(/\.[^/.]+$/, ''));
      toast.success('Arkaplan yüklendi, isim verip "Ekle"ye basın');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploadingBg(false);
    }
  };

  const handleAddBackground = async () => {
    if (!newBgName.trim() || !newBgUrl) {
      toast.error('İsim ve resim gerekli');
      return;
    }
    try {
      await axiosInstance.post(`/teams/${selectedTeam}/qr-poster/backgrounds`, {
        name: newBgName.trim(),
        image_url: newBgUrl
      });
      toast.success('Arkaplan eklendi');
      setNewBgName('');
      setNewBgUrl('');
      loadBackgrounds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ekleme başarısız');
    }
  };

  const handleDeleteBackground = async (id) => {
    if (!window.confirm('Arkaplanı silmek istediğinize emin misiniz?')) return;
    try {
      await axiosInstance.delete(`/teams/${selectedTeam}/qr-poster/backgrounds/${id}`);
      toast.success('Arkaplan silindi');
      loadBackgrounds();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <QrCode className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Ayarlar için önce bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="qr-poster-settings-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">QR Poster Ayarları</h1>
          <p className="text-gray-600">A4 QR posterlerinde kullanılan mağaza bilgileri ve arkaplanlar</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
          data-testid="save-qr-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store info */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white">
            <CardTitle>Mağaza Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label>Logo</Label>
              {settings.logo_url ? (
                <div className="relative inline-block">
                  <img src={settings.logo_url} alt="Logo" className="max-h-32 rounded-2xl border border-gray-200 bg-gray-100 p-2" />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full"
                    onClick={() => setSettings({ ...settings, logo_url: '' })}
                    data-testid="remove-logo-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} className="hidden" data-testid="logo-upload-input" />
                  <div className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${uploadingLogo ? 'bg-gray-100 border-gray-300' : 'border-amber-300 hover:border-amber-500 hover:bg-amber-50'}`}>
                    <Upload className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-600">{uploadingLogo ? 'Yükleniyor...' : 'Logo Yükle (PNG önerilir)'}</span>
                  </div>
                </label>
              )}
            </div>
            <div className="space-y-2">
              <Label>Logo Boyutu (%): {settings.logo_size_percent}</Label>
              <input
                type="range"
                min="5"
                max="60"
                value={settings.logo_size_percent}
                onChange={(e) => setSettings({ ...settings, logo_size_percent: parseInt(e.target.value) })}
                className="w-full accent-amber-500"
                data-testid="logo-size-slider"
              />
              <p className="text-xs text-gray-500">Posterin genişliğine göre logo genişlik yüzdesi (5-60)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_name">Mağaza Adı</Label>
              <Input id="store_name" value={settings.store_name || ''} onChange={(e) => setSettings({ ...settings, store_name: e.target.value })} placeholder="DELFIEE STORE" data-testid="store-name-input" />
              <p className="text-xs text-gray-500">Logo yüklüyse mağaza adı ve alt başlık posterde görünmez (logo tek başına marka kimliği olur).</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_subtitle">Alt Başlık</Label>
              <Input id="store_subtitle" value={settings.store_subtitle || ''} onChange={(e) => setSettings({ ...settings, store_subtitle: e.target.value })} placeholder="BY DUMAN" data-testid="store-subtitle-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_display">Alt Kısım Telefon Metni</Label>
              <Input id="phone_display" value={settings.phone_display || ''} onChange={(e) => setSettings({ ...settings, phone_display: e.target.value })} placeholder="+90 554 154 18 34" data-testid="phone-display-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="accent_color">Vurgu Rengi (metin/QR)</Label>
                <Input id="accent_color" type="color" value={settings.accent_color} onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })} className="h-10 p-1" data-testid="accent-color-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="text_color">Etiket Rengi</Label>
                <Input id="text_color" type="color" value={settings.text_color} onChange={(e) => setSettings({ ...settings, text_color: e.target.value })} className="h-10 p-1" data-testid="text-color-input" />
              </div>
            </div>
            
            {/* Ön Yüz (Standart Poster) Ayarları */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-purple-600 mb-3">📄 Ön Yüz (Standart Poster)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="qr_size_percent">QR Boyutu (%{settings.qr_size_percent || 100})</Label>
                  <input 
                    type="range" 
                    id="qr_size_percent"
                    min="50" 
                    max="150" 
                    value={settings.qr_size_percent || 100} 
                    onChange={(e) => setSettings({ ...settings, qr_size_percent: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    data-testid="qr-size-slider"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qr_label_size">QR Etiketi Boyutu (%{settings.qr_label_size || 100})</Label>
                  <input 
                    type="range" 
                    id="qr_label_size"
                    min="50" 
                    max="150" 
                    value={settings.qr_label_size || 100} 
                    onChange={(e) => setSettings({ ...settings, qr_label_size: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    data-testid="qr-label-size-slider"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="mb-2 block">Etiket Pozisyonu</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, qr_label_position: 'below' })}
                    className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium transition-all ${settings.qr_label_position !== 'above' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="front-label-below"
                  >
                    QR Altında
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, qr_label_position: 'above' })}
                    className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium transition-all ${settings.qr_label_position === 'above' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="front-label-above"
                  >
                    QR Üstünde
                  </button>
                </div>
              </div>
            </div>
            
            {/* Arka Yüz (Marka Telegram) Ayarları */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-amber-600 mb-3">📄 Arka Yüz (Marka Telegram)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="back_qr_size_percent">QR Boyutu (%{settings.back_qr_size_percent || 100})</Label>
                  <input 
                    type="range" 
                    id="back_qr_size_percent"
                    min="50" 
                    max="150" 
                    value={settings.back_qr_size_percent || 100} 
                    onChange={(e) => setSettings({ ...settings, back_qr_size_percent: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    data-testid="back-qr-size-slider"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="back_qr_label_size">QR Etiketi Boyutu (%{settings.back_qr_label_size || 100})</Label>
                  <input 
                    type="range" 
                    id="back_qr_label_size"
                    min="50" 
                    max="150" 
                    value={settings.back_qr_label_size || 100} 
                    onChange={(e) => setSettings({ ...settings, back_qr_label_size: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    data-testid="back-qr-label-size-slider"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="mb-2 block">Etiket Pozisyonu</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, back_qr_label_position: 'below' })}
                    className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium transition-all ${settings.back_qr_label_position !== 'above' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="back-label-below"
                  >
                    QR Altında
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, back_qr_label_position: 'above' })}
                    className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium transition-all ${settings.back_qr_label_position === 'above' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600'}`}
                    data-testid="back-label-above"
                  >
                    QR Üstünde
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label htmlFor="brand_title_size">Marka Başlık Boyutu (%{settings.brand_title_size || 100})</Label>
                  <input 
                    type="range" 
                    id="brand_title_size"
                    min="50" 
                    max="150" 
                    value={settings.brand_title_size || 100} 
                    onChange={(e) => setSettings({ ...settings, brand_title_size: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    data-testid="brand-title-size-slider"
                  />
                  <p className="text-xs text-gray-500">QR üstündeki marka adı</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand_title_offset">Başlık Pozisyonu ({settings.brand_title_offset || 0}px)</Label>
                  <input 
                    type="range" 
                    id="brand_title_offset"
                    min="-400" 
                    max="400" 
                    value={settings.brand_title_offset || 0} 
                    onChange={(e) => setSettings({ ...settings, brand_title_offset: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    data-testid="brand-title-offset-slider"
                  />
                  <p className="text-xs text-gray-500">Negatif=yukarı, Pozitif=aşağı (-400 ile +400 arası)</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label htmlFor="phone_size_percent">Telefon Boyutu (%{settings.phone_size_percent || 100})</Label>
                  <input 
                    type="range" 
                    id="phone_size_percent"
                    min="50" 
                    max="150" 
                    value={settings.phone_size_percent || 100} 
                    onChange={(e) => setSettings({ ...settings, phone_size_percent: parseInt(e.target.value) })} 
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                    data-testid="phone-size-slider"
                  />
                  <p className="text-xs text-gray-500">Her iki yüzde de geçerli</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social/QR info */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardTitle>QR İçerikleri</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-xs text-gray-500 bg-purple-50 p-2 rounded-lg">
              Devre dışı bıraktığınız QR&apos;lar posterde görünmez, düzen otomatik ayarlanır (1/2/3/4/5 QR).
            </p>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
              <input type="checkbox" checked={settings.use_dynamic_qr} onChange={(e) => setSettings({ ...settings, use_dynamic_qr: e.target.checked })} className="w-5 h-5 accent-blue-500" data-testid="use-dynamic-qr-toggle" />
              <div className="flex-1">
                <p className="text-sm font-semibold">Dinamik QR modu</p>
                <p className="text-xs text-gray-600">Açık: QR&apos;lar bizim sunucumuza yönlendirir (bilgi değiştiğinde eski baskılar da güncel). Kapalı: QR&apos;lar direkt Instagram/WhatsApp/Telegram URL&apos;lerini içerir (baskıda hmaminal.com değil, doğrudan link görünür).</p>
              </div>
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label htmlFor="whatsapp_number" className="font-semibold">WhatsApp</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.whatsapp_active} onChange={(e) => setSettings({ ...settings, whatsapp_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="wa-active-toggle" />
                  <span className="text-xs font-medium">{settings.whatsapp_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <Input id="whatsapp_number" value={settings.whatsapp_number || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} placeholder="+905541541834" data-testid="wa-number-input" />
              <Textarea value={settings.whatsapp_message || ''} onChange={(e) => setSettings({ ...settings, whatsapp_message: e.target.value })} placeholder="Hazır mesaj (opsiyonel): Merhaba, sipariş vermek istiyorum." rows={2} data-testid="wa-message-input" />
              <Input value={settings.whatsapp_label || ''} onChange={(e) => setSettings({ ...settings, whatsapp_label: e.target.value })} placeholder="Poster başlığı: WhatsApp" data-testid="wa-label-input" />
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Telegram (Marka)</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.telegram_active} onChange={(e) => setSettings({ ...settings, telegram_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="tg-active-toggle" />
                  <span className="text-xs font-medium">{settings.telegram_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <Label className="text-xs font-normal">Standart posterde Telegram QR&apos;ı hangi kaynağı göstersin?</Label>
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="tg-source" value="brand_link" checked={settings.brand_telegram_source === 'brand_link'} onChange={() => setSettings({ ...settings, brand_telegram_source: 'brand_link' })} className="accent-purple-500" data-testid="tg-source-brand" />
                  Markanın kendi linki (her marka için farklı)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="tg-source" value="main_channel" checked={settings.brand_telegram_source === 'main_channel'} onChange={() => setSettings({ ...settings, brand_telegram_source: 'main_channel' })} className="accent-purple-500" data-testid="tg-source-main" />
                  Ana kanal linki (Mağazalar sayfasındaki &quot;Ana Kanal URL&quot;)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="tg-source" value="custom" checked={settings.brand_telegram_source === 'custom'} onChange={() => setSettings({ ...settings, brand_telegram_source: 'custom' })} className="accent-purple-500" data-testid="tg-source-custom" />
                  Manuel URL
                </label>
              </div>
              {settings.brand_telegram_source === 'custom' && (
                <Input value={settings.brand_telegram_custom_url || ''} onChange={(e) => setSettings({ ...settings, brand_telegram_custom_url: e.target.value })} placeholder="https://t.me/..." data-testid="tg-custom-url-input" />
              )}
              <Label className="text-xs font-normal mt-2">Varsayılan (yukarıdaki seçim geçersiz kalırsa fallback)</Label>
              <Input value={settings.telegram_default_url || ''} onChange={(e) => setSettings({ ...settings, telegram_default_url: e.target.value })} placeholder="https://t.me/delfieestore" data-testid="tg-default-input" />
              <Input value={settings.telegram_label || ''} onChange={(e) => setSettings({ ...settings, telegram_label: e.target.value })} placeholder="Poster başlığı: Telegram" data-testid="tg-label-input" />
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label htmlFor="instagram_url" className="font-semibold">Instagram</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.instagram_active} onChange={(e) => setSettings({ ...settings, instagram_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="ig-active-toggle" />
                  <span className="text-xs font-medium">{settings.instagram_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <Input id="instagram_url" value={settings.instagram_url || ''} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} placeholder="https://instagram.com/delfieestore" data-testid="ig-url-input" />
              <Input value={settings.instagram_label || ''} onChange={(e) => setSettings({ ...settings, instagram_label: e.target.value })} placeholder="Poster başlığı: Instagram" data-testid="ig-label-input" />
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">WiFi</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.wifi_active} onChange={(e) => setSettings({ ...settings, wifi_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="wifi-active-toggle" />
                  <span className="text-xs font-medium">{settings.wifi_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">SSID</Label>
                  <Input value={settings.wifi_ssid || ''} onChange={(e) => setSettings({ ...settings, wifi_ssid: e.target.value })} placeholder="DelfieeGuest" data-testid="wifi-ssid-input" />
                </div>
                <div>
                  <Label className="text-xs">Şifre</Label>
                  <Input value={settings.wifi_password || ''} onChange={(e) => setSettings({ ...settings, wifi_password: e.target.value })} placeholder="şifre" data-testid="wifi-password-input" />
                </div>
                <div>
                  <Label className="text-xs">Şifreleme</Label>
                  <Select value={settings.wifi_encryption} onValueChange={(v) => setSettings({ ...settings, wifi_encryption: v })}>
                    <SelectTrigger data-testid="wifi-encryption-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WPA">WPA/WPA2</SelectItem>
                      <SelectItem value="WEP">WEP</SelectItem>
                      <SelectItem value="nopass">Şifresiz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Poster Başlığı</Label>
                  <Input value={settings.wifi_label || ''} onChange={(e) => setSettings({ ...settings, wifi_label: e.target.value })} placeholder="WiFi" data-testid="wifi-label-input" />
                </div>
              </div>
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Ana Kanal</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.main_channel_active} onChange={(e) => setSettings({ ...settings, main_channel_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="mc-active-toggle" />
                  <span className="text-xs font-medium">{settings.main_channel_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <p className="text-xs text-gray-500">Standart posterde ek QR olarak ana kanal linki. URL, Mağazalar sayfasındaki &quot;Ana Kanal URL&quot; alanından alınır (boşsa varsayılan Telegram URL fallback).</p>
              <Input value={settings.main_channel_label || ''} onChange={(e) => setSettings({ ...settings, main_channel_label: e.target.value })} placeholder="Poster başlığı: Ana Kanal" data-testid="mc-label-input" />
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-gray-50">
              <div className="flex items-center justify-between">
                <Label htmlFor="website_url" className="font-semibold">Website</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.website_active} onChange={(e) => setSettings({ ...settings, website_active: e.target.checked })} className="w-4 h-4 accent-purple-500" data-testid="website-active-toggle" />
                  <span className="text-xs font-medium">{settings.website_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <Input id="website_url" value={settings.website_url || ''} onChange={(e) => setSettings({ ...settings, website_url: e.target.value })} placeholder="https://delfieestore.com" data-testid="website-url-input" />
              <Input value={settings.website_label || ''} onChange={(e) => setSettings({ ...settings, website_label: e.target.value })} placeholder="Poster başlığı: Website" data-testid="website-label-input" />
            </div>

            <div className="space-y-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Marka Website (Sadece Marka Telegram şablonunda 2. QR)</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.brand_website_active} onChange={(e) => setSettings({ ...settings, brand_website_active: e.target.checked })} className="w-4 h-4 accent-amber-500" data-testid="brand-website-active-toggle" />
                  <span className="text-xs font-medium">{settings.brand_website_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <p className="text-xs text-gray-600">URL şablonu — Marka adı slugify edilerek yerine geçer. <code className="bg-white px-1 rounded">{'{website}'}</code> = yukarıdaki Website URL, <code className="bg-white px-1 rounded">{'{brand}'}</code> = marka slug&apos;ı (<code className="bg-white px-1 rounded">*</code> da <code>{'{website}'}</code> ile aynı). Örn: <code className="bg-white px-1 rounded">{'{website}/collections/{brand}'}</code></p>
              <Input value={settings.brand_website_pattern || ''} onChange={(e) => setSettings({ ...settings, brand_website_pattern: e.target.value })} placeholder="{website}/collections/{brand}" data-testid="brand-website-pattern-input" />
              <Input value={settings.brand_website_label || ''} onChange={(e) => setSettings({ ...settings, brand_website_label: e.target.value })} placeholder="Poster başlığı: Website" data-testid="brand-website-label-input" />
            </div>

          </CardContent>
        </Card>
      </div>

      {/* Backgrounds */}
      <Card className="shadow-lg border-0 overflow-hidden mt-6">
        <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-900 text-white">
          <CardTitle>Arkaplan Kütüphanesi</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-3 p-4 bg-slate-50 rounded-2xl">
            <div className="flex-1 space-y-2">
              <Label>Arkaplan Adı</Label>
              <Input value={newBgName} onChange={(e) => setNewBgName(e.target.value)} placeholder="Örn: Bordo Kadife" data-testid="new-bg-name-input" />
            </div>
            <div className="flex-1 space-y-2">
              <Label>Resim URL veya Yükle (A4 dikey önerilir: 2480×3508 px @ 300 DPI)</Label>
              <div className="flex gap-2">
                <Input value={newBgUrl} onChange={(e) => setNewBgUrl(e.target.value)} placeholder="https://..." data-testid="new-bg-url-input" />
                <label>
                  <input type="file" accept="image/*" onChange={handleBgFileUpload} disabled={uploadingBg} className="hidden" data-testid="new-bg-upload-input" />
                  <Button asChild variant="outline" disabled={uploadingBg}>
                    <span><Upload className="h-4 w-4 mr-1" />{uploadingBg ? '...' : 'Yükle'}</span>
                  </Button>
                </label>
              </div>
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddBackground} className="bg-slate-800 text-white hover:bg-slate-700" data-testid="add-bg-button">Ekle</Button>
            </div>
          </div>

          {backgrounds.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
              Henüz arkaplan yok. Yukarıdan bir tane ekleyin.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {backgrounds.map(bg => (
                <div key={bg.id} className="relative group border border-gray-200 rounded-2xl overflow-hidden" data-testid={`bg-item-${bg.id}`}>
                  <img src={bg.image_url} alt={bg.name} className="w-full h-40 object-cover" />
                  <div className="p-2 text-sm font-medium truncate">{bg.name}</div>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteBackground(bg.id)}
                    data-testid={`delete-bg-${bg.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
